import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  LoadingSpinner,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { PiBoatThin, PiEyeSlash } from 'react-icons/pi';
import { useInfiniteScroll } from '@ais-ui/custom-hooks';
import { AisSearchResult, ShipSearchResult, useShipSearch } from './useShipSearch';
import { useLocalization } from '@ais-ui/locale-management';
import { RecentSearch, useRecentSearches } from '../../../components/RecentSearchesProvider';
import { RecentVesselSearches } from './RecentVesselSearches';

export const useInfiniteSearchShips = (limit: number, searchString: string) => {
  const { data, isLoading, isError } = useShipSearch(searchString);
  const { viewportRef, offset, setOffset } = useInfiniteScroll(limit);

  useEffect(() => {
    setOffset(0);
  }, [searchString, setOffset]);

  const uniqueShips = useMemo(() => {
    const ships = data ? data : [];
    const shipMap = new Map<string, ShipSearchResult[]>();
    // Populate the map with ships grouped by MMSI and IMO
    ships.forEach((ship) => {
      const key = `${ship.mmsi}-${ship.imo}`;

      if (!shipMap.has(key)) {
        shipMap.set(key, []);
      }
      shipMap.get(key)?.push(ship);
    });

    return ships.filter((ship) => {
      const key = `${ship.mmsi}-${ship.imo}`;

      const shipsWithSameMmsiImo = shipMap.get(key);

      if (shipsWithSameMmsiImo && shipsWithSameMmsiImo.length > 1) {
        // If there is more than one ship with the same MMSI and IMO
        // and this ship's request type is 'database', we filter it out
        if (ship.requestType === 'database') {
          return false;
        }
      }

      return true;
    });
  }, [data]);

  const [displayedShips, setDisplayedShips] = useState<ShipSearchResult[]>([]);

  const memoizedShips = useMemo(
    () => uniqueShips.slice(0, limit),
    [uniqueShips, limit],
  );

  useEffect(() => {
    if (isLoading) {
      if (displayedShips.length > 0) {
        setDisplayedShips([]);
      }
      return;
    }

    if (searchString.length < 3) {
      if (displayedShips.length > 0) {
        setDisplayedShips([]);
      }
      return;
    }

    // If offset is 0, return the first limit number of ships directly
    if (offset === 0) {
      // Only update state if new ships are different
      if (JSON.stringify(displayedShips) !== JSON.stringify(memoizedShips)) {
        setDisplayedShips(memoizedShips);
      }
      return;
    }

    const uniqueShipIds = new Set(
      displayedShips.map((ship: ShipSearchResult) => ship.mmsi),
    );

    const start = displayedShips.length;
    const end = displayedShips.length + limit;

    // Filter out ships that are already in the Set
    const filteredShips = (uniqueShips.slice(start, end) || []).filter(
      (ship) => !uniqueShipIds.has(ship.mmsi),
    );

    // Only update state if new ships were actually added
    if (filteredShips.length > 0) {
      setDisplayedShips((prevShips) => [...prevShips, ...filteredShips]);
    }
  }, [
    uniqueShips,
    offset,
    isLoading,
    searchString,
    limit,
    displayedShips,
    memoizedShips,
  ]);

  return {
    displayedShips,
    viewportRef,
    setOffset,
    isLoading,
    uniqueShips,
  };
};

export interface AISShipSearchHitsProps {
  searchString: string;
  onClick: (ship: ShipSearchResult) => void;
}

export const AISShipSearch = ({
  searchString,
  onClick,
}: AISShipSearchHitsProps) => {
  const { messages } = useLocalization();
  const { addRecentSearch } = useRecentSearches();
  const { displayedShips, uniqueShips, viewportRef, isLoading } =
    useInfiniteSearchShips(25, searchString);

  const scrollAreaHeightClass = useMemo(() => {
    const itemHeight = 52; // Height for each item in pixels
    const maxHeight = 256; // Maximum allowed height in pixels

    if (!displayedShips || displayedShips.length === 0) return '';

    const totalHeight = Math.min(displayedShips.length * itemHeight, maxHeight);

    return `h-[${totalHeight}px]`;
  }, [displayedShips]);

  const handleShipClick = useCallback(
    (ship: ShipSearchResult) => {
      // Store all identity fields so we can reconstruct the same object later
      // Use 'in' operator to safely access properties that may not exist on all union members
      addRecentSearch({
        id: String(ship.mmsi || ship.name),
        name: ship.name || '',
        type: 'vessel',
        metadata: {
          mmsi: ship.mmsi ?? undefined,
          imo: ship.imo ?? undefined,
          callSign: 'callSign' in ship ? ship.callSign ?? undefined : undefined,
          shipType: 'type' in ship ? ship.type ?? undefined : undefined,
          requestType: ship.requestType,
        },
      });
      onClick(ship);
    },
    [addRecentSearch, onClick]
  );

  // Reconstruct AisSearchResult from stored metadata - same data, correct types
  const handleRecentSearchClick = useCallback(
    (search: RecentSearch) => {
      const ship: AisSearchResult = {
        name: search.name,
        mmsi: search.metadata.mmsi != null ? String(search.metadata.mmsi) : null,
        imo: search.metadata.imo ?? null,
        callSign: search.metadata.callSign ?? null,
        type: search.metadata.shipType ?? null,
        requestType: search.metadata.requestType === 'ais' || search.metadata.requestType === 'no-recent-ais' 
          ? search.metadata.requestType 
          : 'ais',
      };
      onClick(ship);
    },
    [onClick]
  );

  return (
    <Command className="h-fit">
      <CommandList className="h-fit ">
        {uniqueShips.length > 0 && (
          <CommandGroup
            heading={`vessels (${uniqueShips.length})`}
          ></CommandGroup>
        )}
        {!isLoading && searchString.length < 3 && (
          <RecentVesselSearches onRecentSearchClick={handleRecentSearchClick} />
        )}
        {!isLoading &&
          searchString.length >= 3 &&
          displayedShips.length === 0 && (
            <div className="py-6 text-center text-sm">{messages.shipSearchNoShipsFound}</div>
          )}
        {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
        {displayedShips && displayedShips.length > 0 && (
          <ScrollArea
            className={cn('h-[256px]', scrollAreaHeightClass)}
            infiniteScrollRef={viewportRef}
          >
            {displayedShips.map((ship, index) => (
              <CommandItem
                className="mr-4 py-2 hover:cursor-pointer hover:bg-transparent data-[selected=true]:bg-transparent group border-b border-border last:border-b-0"
                onSelect={() => handleShipClick(ship)}
                key={index}
              >
                <div className="flex flex-col space-y-1 w-full">
                  <div className="flex justify-between space-x-1">
                    <div className="flex items-center space-x-1">
                      <PiBoatThin
                        className={cn(
                          'mr-2 size-4',
                          ship.requestType === 'database' && 'text-red-500',
                          ship.requestType === 'ship-db' && 'text-purple-500',
                          ship.requestType === 'ais' && 'text-blue-600',
                          ship.requestType === 'no-recent-ais' &&
                            'text-yellow-500',
                        )}
                      />
                      <span className="group-hover:underline">{ship.name}</span>
                    </div>
                    <div className="flex space-x-2 items-center">
                      {ship.requestType === 'database' && (
                        <div className="text-muted-foreground text-xs">
                          no signal
                        </div>
                      )}
                      {ship.requestType === 'no-recent-ais' && (
                        <div className="text-muted-foreground text-xs">
                          no recent AIS
                        </div>
                      )}
                      {ship.requestType === 'ship-db' && (
                        <div className="text-muted-foreground text-xs">
                          Data from EVI
                        </div>
                      )}
                      {ship.filteredOut && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <PiEyeSlash className="h-3 w-3 text-yellow-600"></PiEyeSlash>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            Not part of the current filter
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 text-xs text-muted-foreground items-center">
                    <div>MMSI: {ship.mmsi}</div>
                    <div
                      className={cn(
                        'rounded-full h-1 w-1',
                        ship.requestType === 'ais' && 'bg-blue-600',
                        ship.requestType === 'no-recent-ais' && 'bg-yellow-500',
                        ship.requestType === 'database' && 'bg-red-500',
                        ship.requestType === 'ship-db' && 'bg-purple-500',
                      )}
                    ></div>
                    <div>IMO: {ship.imo ? ship.imo : 'None'}</div>
                  </div>
                </div>
              </CommandItem>
            ))}
          </ScrollArea>
        )}
      </CommandList>
    </Command>
  );
};
